import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch } from '~/behaviors/redux';
import { useTranslation } from '~/behaviors/languages';
import { accountActions } from '~/behaviors/actions/account.actions';
import { jss } from '~/tools/jss';
import { StatusDialog, Loading } from '../UI.shared';
import { urls } from '~/tools/urls';
import { createSsoReference, reportSsoFailure } from './SSO.helpers';

const useStyles = jss.makeStyles(() => ({
  container: {
    height: 200,
  },
}));

type DialogState = {
  status: 'info' | 'error';
  title: string;
  message: string;
  isVisible: boolean;
  closeButtonLabel: string;
};

const defaultDialogState: DialogState = {
  status: 'info',
  title: '',
  message: '',
  isVisible: false,
  closeButtonLabel: '',
};

export const SSOPage = () => {
  // ------------------------------------------------------------------ State --
  const dispatch = useAppDispatch();
  const styles = useStyles();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<DialogState>(defaultDialogState);
  const { t } = useTranslation();
  const location = useLocation();
  const [kazooLoginUrl, setKazooLoginUrl] = useState<string | null>(null);

  const searchParams = urls.parseQueryString(location.search);
  const hashParams = urls.parseQueryString(location.hash.slice(1));
  const params = { ...searchParams, ...hashParams };
  const clientId = params?.client_id;
  const idToken = params?.id_token;
  const isMissingToken = !clientId || !idToken;

  // Parsed without comma splitting, because Cognito's error_description is prose that can contain commas.
  const errorParams = {
    ...urls.parseQueryString(location.search, { comma: false }),
    ...urls.parseQueryString(location.hash.slice(1), { comma: false }),
  };
  const ssoError = errorParams.error;
  const ssoErrorDescription = errorParams.error_description;
  const [ssoReference] = useState(createSsoReference);

  // -------------------------------------------------------------- Lifecycle --
  useEffect(() => {
    if (!isMissingToken) return;

    reportSsoFailure({
      reference: ssoReference,
      clientId,
      error: ssoError,
      errorDescription: ssoErrorDescription,
    });
  }, [isMissingToken, ssoReference, clientId, ssoError, ssoErrorDescription]);

  useEffect(() => {
    if (isMissingToken) return;

    dispatch(
      accountActions.loginWithSSO({
        clientId,
        ssoToken: idToken,
      }),
    ).then((action) => {
      if (action.error) {
        const error = action.payload;
        if (error.code === 'PULLGN0001') {
          setKazooLoginUrl(error?.customObj?.kazooLoginUrl);
          return setDialog({
            status: 'error',
            title: t('sso.loginFailed'),
            message: t('sso.loginFailed.invalidToken'),
            closeButtonLabel: t('button.back'),
            isVisible: true,
          });
        }
        setDialog({
          status: 'error',
          title: t('user.not.found'),
          message: t('sso.user.not.found.description', { email: error?.customObj?.email }),
          closeButtonLabel: t('back.to.login'),
          isVisible: true,
        });
      }
    });
  }, [isMissingToken, clientId, idToken, dispatch, t]);

  // ----------------------------------------------------------------- Events --
  const handleDialogClose = () => {
    if (kazooLoginUrl) {
      window.location.href = kazooLoginUrl;
      return;
    }
    navigate('/login');
  };

  // -------------------------------------------------------------- Interface --
  if (isMissingToken) {
    return <Navigate to={`/login?ssoRef=${ssoReference}`} replace />;
  }

  return (
    <div data-testid="sso-page">
      <StatusDialog
        status={dialog.status}
        title={dialog.title}
        open={dialog.isVisible}
        onClose={handleDialogClose}
        closeButtonLabel={dialog.closeButtonLabel}
      >
        {dialog.message}
      </StatusDialog>

      <div className={styles.container}>
        <Loading />
      </div>
    </div>
  );
};
